import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { patientsAPI } from '../lib/api';
import { toast } from 'sonner';
import { Star, Users, UserCheck, Building2, Phone, Calendar, IndianRupee, ChevronRight, Crown, AlertCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { cn } from '../lib/utils';

const TIER_STYLES = {
  platinum: 'bg-slate-100 text-slate-700 border-slate-300',
  gold: 'bg-amber-100 text-amber-700 border-amber-300',
  silver: 'bg-zinc-100 text-zinc-700 border-zinc-300',
  corporate: 'bg-blue-100 text-blue-700 border-blue-300',
  celebrity: 'bg-purple-100 text-purple-700 border-purple-300',
  other: 'bg-slate-100 text-slate-700 border-slate-300',
};

const VipDashboardPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await patientsAPI.vipDashboard();
        setData(res.data);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to load VIP dashboard');
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) {
    return <div className="space-y-4">{[1,2,3,4].map(i => <div key={i} className="h-24 bg-slate-200 rounded-xl animate-pulse" />)}</div>;
  }
  if (!data) {
    return <div className="text-center py-16 text-slate-400">No data</div>;
  }

  const filtered = (data.patients || []).filter(p => {
    if (!search) return true;
    const q = search.toLowerCase();
    return p.first_name?.toLowerCase().includes(q) || p.last_name?.toLowerCase().includes(q) || p.patient_id?.toLowerCase().includes(q) || p.phone?.includes(q);
  });

  const kpis = [
    { label: 'Total VIP Patients', value: data.total_vip, icon: Crown, bg: 'bg-amber-500' },
    { label: 'Active VIP', value: data.active_vip, icon: UserCheck, bg: 'bg-emerald-500' },
    { label: 'In-House Today', value: data.in_house_today, icon: Building2, bg: 'bg-blue-500' },
    { label: 'Total VIP Revenue', value: (data.patients || []).reduce((s, p) => s + (p.total_paid || 0), 0), icon: IndianRupee, bg: 'bg-violet-500', prefix: 'Rs.' },
  ];

  return (
    <div className="space-y-6" data-testid="vip-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Star className="h-6 w-6 fill-amber-400 text-amber-500" /> VIP Patients
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Full journey for every VIP — appointments, visits, billing & admission status at a glance.</p>
        </div>
        <div className="relative w-72">
          <Input placeholder="Search VIP by name, ID, phone..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="vip-search" />
          <Star size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-amber-400" />
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k, i) => (
          <Card key={i} className="border-slate-200" data-testid={`vip-kpi-${i}`}>
            <CardContent className="p-4 flex items-center gap-4">
              <div className={cn('h-12 w-12 rounded-xl flex items-center justify-center', k.bg)}>
                <k.icon size={22} className="text-white" />
              </div>
              <div>
                <p className="text-xs text-slate-400 font-medium uppercase">{k.label}</p>
                <p className="text-2xl font-bold text-slate-900">{k.prefix || ''}{typeof k.value === 'number' ? k.value.toLocaleString('en-IN') : k.value}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-dashed border-slate-200 p-12 text-center text-slate-400">
          <Star size={40} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm">{search ? 'No VIP patients match your search.' : 'No VIP patients yet. Mark a patient as VIP from the Patients page to see them here.'}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(p => (
            <div key={p.id} className="bg-white border border-slate-200 rounded-xl p-5 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate(`/patients/${p.id}`)}
              data-testid={`vip-patient-${p.patient_id}`}>
              <div className="flex items-start gap-4">
                <div className="relative flex-shrink-0">
                  <div className="h-14 w-14 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white font-bold text-lg">
                    {(p.first_name?.[0] || '') + (p.last_name?.[0] || '')}
                  </div>
                  <Star size={14} className="absolute -bottom-0.5 -right-0.5 fill-amber-400 text-amber-500 bg-white rounded-full p-0.5 border border-amber-200" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="font-bold text-slate-900">{p.title ? `${p.title} ` : ''}{p.first_name} {p.last_name}</h3>
                    {p.vip_tier && (
                      <Badge variant="outline" className={cn('text-[10px] uppercase', TIER_STYLES[p.vip_tier] || TIER_STYLES.other)}>
                        {p.vip_tier}
                      </Badge>
                    )}
                    <span className="text-xs font-mono text-slate-400">{p.patient_id}</span>
                    {p.current_admission && (
                      <Badge className="bg-red-100 text-red-700 text-[10px]"><AlertCircle size={10} className="mr-0.5" /> In-Patient</Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-xs text-slate-500 mt-1.5 flex-wrap">
                    {p.phone && <span className="flex items-center gap-1"><Phone size={11} /> {p.phone}</span>}
                    {p.blood_group && <span>Blood: {p.blood_group}</span>}
                    {p.gender && <span className="capitalize">{p.gender}</span>}
                  </div>
                  {p.vip_notes && (
                    <p className="text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded mt-2 inline-block">
                      Note: {p.vip_notes}
                    </p>
                  )}
                </div>

                {/* Right-side journey strip */}
                <div className="hidden md:grid grid-cols-4 gap-3 text-center min-w-[420px]">
                  <div className="bg-slate-50 rounded-lg p-2">
                    <p className="text-[10px] uppercase text-slate-400 font-semibold">Visits</p>
                    <p className="text-lg font-bold text-slate-800">{p.visits_count || 0}</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-2">
                    <p className="text-[10px] uppercase text-blue-600 font-semibold">Appts</p>
                    <p className="text-lg font-bold text-blue-700">{p.appointments_count || 0}</p>
                  </div>
                  <div className="bg-emerald-50 rounded-lg p-2">
                    <p className="text-[10px] uppercase text-emerald-600 font-semibold">Paid</p>
                    <p className="text-sm font-bold text-emerald-700">Rs.{((p.total_paid || 0) / 1000).toFixed(1)}K</p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-2">
                    <p className="text-[10px] uppercase text-purple-600 font-semibold">Last Visit</p>
                    <p className="text-[11px] font-semibold text-purple-700 truncate">
                      {p.last_appointment?.appointment_date || '—'}
                    </p>
                    {p.last_appointment?.doctor_name && <p className="text-[9px] text-slate-400 truncate">Dr. {p.last_appointment.doctor_name}</p>}
                  </div>
                </div>
                <ChevronRight size={16} className="text-slate-300 mt-2 flex-shrink-0" />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default VipDashboardPage;
